// Hook que carga las sesiones del kine activo, en estados terminales
// ('completada' / 'cancelada' / 'interrumpida'), ordenadas por started_at
// desc. Usado por SesionesKine.tsx (Opción 1: vista de ejecuciones, sin
// agenda — ver docs/decisions/sesioneskine-modelo.md).
//
// Filtros:
// - kine_id resuelto via useCurrentKineId() — fallback a TEMP pre-B8.
// - estado != 'en_progreso': sesiones in-progress son ruido temporal en
//   un listado histórico. Si en el futuro se necesita "qué pacientes
//   están haciendo ejercicio AHORA", esa es una notificación in-time
//   distinta, no un listado.
//
// JOINs:
// - ejercicios.nombre para mostrar nombre del ejercicio (no su id).
// - pacientes.nombre + apellido para mostrar nombre completo del
//   paciente — la dimensión que diferencia la vista del kine de la del
//   paciente (que solo ve sus propias sesiones, ver useHistorialSesiones).

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentKineId } from "@/app/components/auth/AuthProvider";

export type SesionEstadoListado = "completada" | "cancelada" | "interrumpida";

export interface SesionKineRow {
  id: string;
  started_at: string;
  ended_at: string | null;
  ejercicio_id: string;
  ejercicio_nombre: string;
  paciente_id: string;
  /** "Nombre Apellido". Si el join falla por algún motivo inesperado,
   *  cae a "Paciente desconocido" — render sigue siendo válido. */
  paciente_nombre: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  estado: SesionEstadoListado;
}

interface State {
  data: SesionKineRow[] | null;
  error: string | null;
  loading: boolean;
}

interface SesionRowRaw {
  id: string;
  started_at: string;
  ended_at: string | null;
  ejercicio_id: string;
  paciente_id: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  estado: SesionEstadoListado;
  ejercicios: { nombre: string } | null;
  pacientes: { nombre: string; apellido: string } | null;
}

export function useSesionesKine(): State {
  const kineId = useCurrentKineId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    // Espera a que useCurrentKineId resuelva (null durante auth loading)
    // para evitar disparar la query con TEMP fallback que rompería con RLS.
    if (!kineId) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select(
          `
          id,
          started_at,
          ended_at,
          ejercicio_id,
          paciente_id,
          total_reps,
          score_promedio,
          duration_sec,
          estado,
          ejercicios!inner(nombre),
          pacientes!inner(nombre, apellido)
        `,
        )
        .eq("kine_id", kineId)
        .neq("estado", "en_progreso")
        .order("started_at", { ascending: false })
        .returns<SesionRowRaw[]>();

      if (cancelled) return;

      if (error) {
        console.warn("[useSesionesKine] error cargando sesiones", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const rows: SesionKineRow[] = (data ?? []).map((r) => ({
        id: r.id,
        started_at: r.started_at,
        ended_at: r.ended_at,
        ejercicio_id: r.ejercicio_id,
        ejercicio_nombre: r.ejercicios?.nombre ?? r.ejercicio_id,
        paciente_id: r.paciente_id,
        paciente_nombre: r.pacientes
          ? `${r.pacientes.nombre} ${r.pacientes.apellido}`.trim()
          : "Paciente desconocido",
        total_reps: r.total_reps,
        score_promedio: r.score_promedio,
        duration_sec: r.duration_sec,
        estado: r.estado,
      }));

      setState({ data: rows, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [kineId]);

  return state;
}
